import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PageHero } from "./About";
import { apiClient } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Mail, Linkedin, MapPin, ArrowRight } from "lucide-react";
import { SectionHeader } from "../components/UI";

const Input = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5568] font-display block mb-2">{label}</span>
    <input data-testid={testId} {...props} className="w-full bg-white border border-slate-200 px-4 py-3 rounded-sm text-[#0B1120] focus:border-[#003399] focus:outline-none focus:ring-1 focus:ring-[#003399] transition-colors"/>
  </label>
);
const Select = ({ label, testId, children, ...props }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5568] font-display block mb-2">{label}</span>
    <select data-testid={testId} {...props} className="w-full bg-white border border-slate-200 px-4 py-3 rounded-sm text-[#0B1120] focus:border-[#003399] focus:outline-none">{children}</select>
  </label>
);
const Textarea = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5568] font-display block mb-2">{label}</span>
    <textarea data-testid={testId} {...props} rows={5} className="w-full bg-white border border-slate-200 px-4 py-3 rounded-sm text-[#0B1120] focus:border-[#003399] focus:outline-none focus:ring-1 focus:ring-[#003399] transition-colors resize-none"/>
  </label>
);

const PartnerForm = () => {
  const { t } = useTranslation();
  const types = t("partner_form.type_options", { returnObjects: true });
  const [form, setForm] = useState({ company_name:"", contact_name:"", role:"", email:"", country:"", website:"", partnership_type: types[0], message:"" });
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);
  const change = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/partner-inquiry", form);
      toast.success(t("partner_form.success"));
      setOk(true);
      setForm({ company_name:"", contact_name:"", role:"", email:"", country:"", website:"", partnership_type: types[0], message:"" });
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="partner-form">
      <Input label={t("partner_form.company")} value={form.company_name} onChange={change("company_name")} required testId="partner-company"/>
      <Input label={t("partner_form.website")} value={form.website} onChange={change("website")} placeholder="https://" testId="partner-website"/>
      <Input label={t("partner_form.contact")} value={form.contact_name} onChange={change("contact_name")} required testId="partner-contact"/>
      <Input label={t("partner_form.role")} value={form.role} onChange={change("role")} required testId="partner-role"/>
      <Input label={t("partner_form.email")} type="email" value={form.email} onChange={change("email")} required testId="partner-email"/>
      <Input label={t("partner_form.country")} value={form.country} onChange={change("country")} required testId="partner-country"/>
      <div className="md:col-span-2">
        <Select label={t("partner_form.type")} value={form.partnership_type} onChange={change("partnership_type")} testId="partner-type">
          {types.map(o => <option key={o} value={o}>{o}</option>)}
        </Select>
      </div>
      <div className="md:col-span-2">
        <Textarea label={t("partner_form.message")} value={form.message} onChange={change("message")} required testId="partner-message"/>
      </div>
      <div className="md:col-span-2 flex justify-end">
        <button type="submit" data-testid="partner-submit" disabled={submitting} className="btn-primary">
          {submitting ? "Sending…" : t("partner_form.submit")} <ArrowRight size={16}/>
        </button>
      </div>
      {ok && <div className="md:col-span-2 text-sm text-[#003399] font-display" data-testid="partner-success">{t("partner_form.success")}</div>}
    </form>
  );
};

const ContactForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name:"", email:"", company:"", subject:"", message:"" });
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);
  const change = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/contact", form);
      toast.success(t("contact.form.success"));
      setOk(true);
      setForm({ name:"", email:"", company:"", subject:"", message:"" });
    } catch { toast.error(t("contact.form.error")); }
    finally { setSubmitting(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="contact-form">
      <Input label={t("contact.form.name")} value={form.name} onChange={change("name")} required testId="contact-name"/>
      <Input label={t("contact.form.email")} type="email" value={form.email} onChange={change("email")} required testId="contact-email"/>
      <Input label={t("contact.form.company")} value={form.company} onChange={change("company")} testId="contact-company"/>
      <Input label={t("contact.form.subject")} value={form.subject} onChange={change("subject")} required testId="contact-subject"/>
      <div className="md:col-span-2"><Textarea label={t("contact.form.message")} value={form.message} onChange={change("message")} required testId="contact-message"/></div>
      <div className="md:col-span-2 flex justify-end">
        <button type="submit" data-testid="contact-submit" disabled={submitting} className="btn-primary">
          {submitting ? "Sending…" : t("contact.form.submit")} <ArrowRight size={16}/>
        </button>
      </div>
      {ok && <div className="md:col-span-2 text-sm text-[#003399] font-display" data-testid="contact-success">{t("contact.form.success")}</div>}
    </form>
  );
};

export default function Contact() {
  const { t } = useTranslation();
  useEffect(() => {
    if (window.location.hash === "#partner") {
      setTimeout(() => document.getElementById("partner")?.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, []);
  return (
    <div data-testid="page-contact">
      <Toaster position="top-right" richColors />
      <PageHero eyebrow={t("contact.eyebrow")} title={t("contact.title")} subtitle={t("contact.subtitle")} image="https://images.pexels.com/photos/8463167/pexels-photo-8463167.jpeg"/>
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4 space-y-8">
            <div>
              <div className="eyebrow">{t("contact.office")}</div>
              <div className="mt-4 space-y-2">
                <div className="flex items-start gap-3 text-[#0B1120]"><MapPin size={16} className="mt-1 text-[#003399]" strokeWidth={1.5}/><span className="text-sm">Via Isaac Newton 9A<br/>20016 Pero, Italy</span></div>
                <div className="flex items-center gap-3 text-[#0B1120]"><Mail size={16} className="text-[#003399]" strokeWidth={1.5}/><a href="mailto:info@eema-group.com" className="text-sm hover:text-[#003399]">info@eema-group.com</a></div>
                <div className="flex items-center gap-3 text-[#0B1120]"><Linkedin size={16} className="text-[#003399]" strokeWidth={1.5}/><a href="https://linkedin.com" target="_blank" rel="noreferrer" className="text-sm hover:text-[#003399]">linkedin.com/company/eema</a></div>
              </div>
            </div>
            <div className="border border-slate-200 rounded-sm overflow-hidden shadow-lg group relative">
              <iframe
                title="EEMA Office - Via Isaac Newton 9A, Pero"
                src="https://maps.google.com/maps?q=Via+Isaac+Newton+9A,+20016+Pero,+Italy&t=m&z=15&output=embed&iwloc=near"
                className="w-full h-80 grayscale-[30%] contrast-[1.05] group-hover:grayscale-0 transition-all duration-500"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md border border-slate-200 shadow-sm px-4 py-3 rounded-sm pointer-events-none">
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#003399] font-display font-medium">EEMA HQ</div>
                <div className="mt-1 text-sm font-display font-semibold text-[#0B1120]">Via Isaac Newton 9A</div>
                <div className="text-xs text-[#4A5568]">20016 Pero — Milan Metro</div>
              </div>
            </div>
            <a
              href="https://maps.google.com/maps?q=Via+Isaac+Newton+9A,+20016+Pero,+Italy"
              target="_blank"
              rel="noreferrer"
              data-testid="open-in-maps"
              className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-[#003399] hover:text-[#00E5FF] font-display transition-colors"
            >
              Open in Google Maps
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7"/><path d="M7 7h10v10"/></svg>
            </a>
          </div>
          <div className="lg:col-span-8" id="contact">
            <SectionHeader eyebrow="General Contact" title="Send a message"/>
            <div className="mt-8"><ContactForm/></div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-24" id="partner">
          <SectionHeader eyebrow={t("partner_form.eyebrow")} title={t("partner_form.title")} subtitle={t("partner_form.subtitle")}/>
          <div className="mt-10"><PartnerForm/></div>
        </div>
      </section>
    </div>
  );
}
